import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  LayoutDashboard,
  Calendar,
  Users,
  Handshake,
  BarChart2,
  Wallet,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Building2,
} from "lucide-react";

import Logo from "../../assets/logos/logo.png";
import LogoText from "../../assets/logos/logo_text.svg";

const navItems = [
  { name: "Overview", path: "/overview", icon: <LayoutDashboard size={18} /> },
  { name: "Events", path: "/events", icon: <Calendar size={18} /> },
  { name: "Attendees", path: "/attendees", icon: <Users size={18} /> },
  {
    name: "Hub Partners",
    path: "/hub-partners",
    icon: <Handshake size={18} />,
  },
  { name: "Reports", path: "/reports", icon: <BarChart2 size={18} /> },
  { name: "Wallet", path: "/wallet", icon: <Wallet size={18} /> },

  { name: "Hub Details", path: "/hub-details", icon: <Building2 size={18} /> },
];

const getInitials = (user) =>
  `${user?.name?.[0] ?? ""}${user?.surname?.[0] ?? ""}`.toUpperCase();

const Sidebar = ({ collapsed, onToggle }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div
      className={`sidebar-transition flex flex-col bg-bg-topbar border-r border-border flex-shrink-0 relative ${
        collapsed ? "w-16" : "w-56"
      }`}
    >
      {/* Logo */}
      <div
        className={`flex items-center h-14 border-b border-border px-4 ${collapsed ? "justify-center" : "gap-3"}`}
      >
        <img src={Logo} alt="Ingroove" className="h-8 mb-3" />

        {!collapsed && (
          <span className="text-sm font-bold ingroove-gradient-text tracking-wide">
            Ingroove
          </span>
        )}
      </div>

      {/* Toggle button */}
      <button
        onClick={onToggle}
        className="absolute -right-3 top-[52px] w-6 h-6 rounded-full bg-bg-elevated border border-border flex items-center justify-center text-text-muted hover:text-primary hover:border-primary transition z-10"
      >
        {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
      </button>

      {/* Nav */}
      <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto overflow-x-hidden mt-2">
        {navItems.map((item) => (
          <NavLink
            key={item.name}
            to={item.path}
            title={collapsed ? item.name : undefined}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-medium transition group relative ${
                isActive
                  ? "bg-primary/10 text-primary border border-primary/20"
                  : "text-text-muted hover:bg-bg-elevated hover:text-text-secondary"
              } ${collapsed ? "justify-center" : ""}`
            }
          >
            <span className="flex-shrink-0">{item.icon}</span>
            {!collapsed && <span>{item.name}</span>}

            {/* Tooltip when collapsed */}
            {collapsed && (
              <div className="absolute left-full ml-3 px-2 py-1 bg-bg-elevated border border-border rounded-lg text-xs text-text-primary whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition z-50">
                {item.name}
              </div>
            )}
          </NavLink>
        ))}
      </nav>

      {/* User + logout */}
      <div className="p-2 border-t border-border">
        {!collapsed && (
          <div className="flex items-center gap-2 px-3 py-2 mb-1">
            <div className="w-7 h-7 rounded-full ingroove-gradient-bg flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
              {getInitials(user)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-text-secondary truncate">
                {user?.name} {user?.surname}
              </p>
              <p className="text-xs text-text-hint truncate">{user?.email}</p>
            </div>
          </div>
        )}

        <button
          onClick={handleLogout}
          title={collapsed ? "Sign out" : undefined}
          className={`flex items-center gap-2 w-full px-3 py-2 rounded-xl text-xs text-red-400 hover:bg-red-500/10 transition ${
            collapsed ? "justify-center" : ""
          }`}
        >
          <LogOut size={15} />
          {!collapsed && "Sign out"}
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
